import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, EMPTY, from, mergeMap, Observable, switchMap, timer, throwError } from 'rxjs';
import { SnackbarService } from '@shared/components/esignatur-snackbar/snackbar.service';
import { SnackbarType } from '@shared/types/snackbar-type.enum';
import { ErrorResponse } from '@shared/types/error-response.interface';
import { SigningErrorResponse } from '@app/shared/types/signing-error-response.interface';
import { SigningErrorContentType } from '@app/shared/types/signing-error-content-type.enum';
import { SIGNING_ERROR_PAGE_MAP } from '@app/shared/types/signing-error-page-map';
import { ERROR_ROUTE } from '@shared/types/error-route.const';
import { SigningErrorStateService } from '@app/services/signing-error-state.service';
import { OrderContextService } from '@services/order-context/order-context.service';

function isSigningErrorResponse(error: unknown): error is SigningErrorResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'exceptionType' in error &&
    Object.values(SigningErrorContentType).includes((error as SigningErrorResponse).exceptionType)
  );
}

export const errorInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const snackbarService = inject(SnackbarService);
  const transLocoService = inject(TranslocoService);
  const signingErrorState = inject(SigningErrorStateService);
  const orderContextService = inject(OrderContextService);
  const router = inject(Router);

  function handleSigningErrorResponse(error: unknown): Observable<never> | null {
    if (!isSigningErrorResponse(error)) return null;

    if (error.exceptionType === SigningErrorContentType.SessionExpired) {
      snackbarService.showToaster(
        SnackbarType.ERROR,
        transLocoService.translate('ERRORS.SESSION_EXPIRED')
      );
      return timer(3000).pipe(
        switchMap(() => {
          const orderId = orderContextService.orderId();
          const signerId = orderContextService.signerId();
          if (orderId && signerId) {
            window.location.reload();
            void router.navigate(['order', orderId, 'signer', signerId]);
          }
          return EMPTY;
        })
      );
    }

    signingErrorState.set(error);
    void router.navigate([ERROR_ROUTE, SIGNING_ERROR_PAGE_MAP[error.exceptionType]]);
    return EMPTY;
  }

  function handleValidationErrors(
    errorResponse: ErrorResponse,
    snackbarService: SnackbarService
  ): void {
    if (errorResponse?.validationErrors && Array.isArray(errorResponse.validationErrors)) {
      for (const fieldError of errorResponse.validationErrors) {
        if (fieldError.errors && Array.isArray(fieldError.errors)) {
          for (const msg of fieldError.errors) {
            snackbarService.showToaster(SnackbarType.ERROR, msg);
          }
        }
      }
    }
  }

  return next(request).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.error instanceof Blob) {
        return from(err.error.text()).pipe(
          mergeMap((text) => {
            const parsed = JSON.parse(text) as SigningErrorResponse;
            return handleError(
              new HttpErrorResponse({ ...err, error: parsed, url: err.url ?? undefined })
            );
          })
        );
      }
      return handleError(err);
    })
  );

  function handleError(err: HttpErrorResponse): Observable<never> {
    const signingErrorResult = handleSigningErrorResponse(err.error);
    if (signingErrorResult !== null) return signingErrorResult;

    const status: HttpStatusCode = err.status;
    const errorResponse = err.error as ErrorResponse;

    switch (status) {
      case HttpStatusCode.Unauthorized:
        snackbarService.showToaster(
          SnackbarType.ERROR,
          transLocoService.translate('ERRORS.UNAUTHORIZED')
        );
        break;

      case HttpStatusCode.BadRequest:
        if (errorResponse?.validationErrors && Array.isArray(errorResponse.validationErrors)) {
          handleValidationErrors(errorResponse, snackbarService);
        } else if (
          err.url?.includes('/verify-otp') ||
          err.url?.includes('/shared-secret') ||
          err.url?.includes('/verify-cpr') ||
          err.url?.endsWith('/questions')
        ) {
          //skipping the toaster
        } else {
          snackbarService.showToaster(
            SnackbarType.ERROR,
            transLocoService.translate('ERRORS.BAD_REQUEST')
          );
        }
        break;

      case HttpStatusCode.Forbidden:
        snackbarService.showToaster(
          SnackbarType.ERROR,
          transLocoService.translate('ERRORS.FORBIDDEN')
        );
        break;

      case HttpStatusCode.NotFound:
        snackbarService.showToaster(
          SnackbarType.ERROR,
          transLocoService.translate('ERRORS.NOT_FOUND')
        );
        break;

      case HttpStatusCode.InternalServerError: {
        const unexpectedSigningErrorResponse: SigningErrorResponse = {
          exceptionType: SigningErrorContentType.Unexpected,
          statusCode: err.status,
        };
        signingErrorState.set(unexpectedSigningErrorResponse);
        void router.navigate([
          ERROR_ROUTE,
          SIGNING_ERROR_PAGE_MAP[SigningErrorContentType.Unexpected],
        ]);
        break;
      }

      default:
        snackbarService.showToaster(
          SnackbarType.ERROR,
          transLocoService.translate('ERRORS.UNKNOWN_ERROR')
        );
        break;
    }

    return throwError(() => err);
  }
};
